import { useAuth0 } from "@auth0/auth0-react"
import { useQuery } from "@tanstack/react-query"
import useLogging from "../hooks/useLogging"
const BATCH_SIZE = 30

const getBatchData = async (token, entries) => {
	const batchData = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/acuity-table-entries`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json"
		},
		body: JSON.stringify({ entries })
	})

	const batch = await batchData.json()

	return batch.payload
}

// Fetch acuity table data

export const fetchAcuityTable = async (token, logging) => {
	try {
		const entriesResponse = await fetch(
			`${process.env.REACT_APP_BACKEND_URL}/api/list-acuity-table-entries`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`
				}
			}
		)

		if (!entriesResponse.ok) {
			throw new Error("Failed to fetch acuity table")
		}

		const entries = (await entriesResponse.json()).payload

		if (!entries) {
			logging.warn("useAcuityTable - Fetching acuity table - No entries found", {
				component: "useAcuityTable",
				function:"fetchAcuityTable"
			})
		}

		const acuityTable = []
		let currentBatch = []
		const promises = []

		for (const entry of entries) {
			currentBatch.push(entry)

			if (currentBatch.length === BATCH_SIZE) {
				promises.push(getBatchData(token, currentBatch))

				currentBatch = []
			}
		}

		if (currentBatch.length > 0) {
			promises.push(getBatchData(token, currentBatch))
		}

		const finishedPromises = await Promise.all(promises)

		for (const batchData of finishedPromises) {
			acuityTable.push(...batchData)
		}

		return acuityTable
	} catch (error) {
		logging.error("Error fetching acuity table:", error)
	}
}

/**
 * This hook, fetches the acuity table
 * @returns {acuityTable, refetch: function}

 */

export const useAcuityTable = () => {
	const { getAccessTokenSilently } = useAuth0()
	const logging = useLogging()
	const { data: acuityTable, refetch } = useQuery({
		queryKey: ["acuityTable"],
		queryFn: async () => {
			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})
			return fetchAcuityTable(token, logging)
		},
		// Data is considered fresh for 60 minutes
		staleTime: 60 * 60 * 1000,
		// Disable refetching when window refocuses
		refetchOnWindowFocus: false
	})

	return { acuityTable, refetch }
}
